import { gql } from "@apollo/client";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EditIcon from "@mui/icons-material/Edit";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";

import { datef } from "@caredna-marlin/utils/dates";
import EditSupplyItem from "./EditSupplyItem";
import SupplyItemPopup, { ACTION_LABEL } from "./SupplyItemPopup";

import { SupplyItemOrderBy, useSupplyItemsPageQuery, OrderDirection, SupplyItemsPageQueryVariables, SupplyItemsPageQuery } from "~/graphql/__generated__/graphql";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission, usePermissionOrRedirect } from "~/hooks/permissions";
import LazyLoadTable, { Column } from "~/components/common/LazyLoadTable";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

const SupplyItemList = () => {
  usePermissionOrRedirect(Permission.useSupplies, "/command");
  const [addSupplyItem, setAddSupplyItem] = useState(false);

  const [variables, setVariables] = useState<SupplyItemsPageQueryVariables>({
    offset: 0,
    orderBy: {
      currentStock: { direction: OrderDirection.Asc, priority: 1 },
    } as SupplyItemOrderBy,
    hideInactive: true
  });

  const { data, loading } = useSupplyItemsPageQuery({ variables });

  const cols: Column<keyof SupplyItemOrderBy>[] = [
    { label: "Label", sort: ["label"] },
    { label: "Stock", sort: ["currentStock"] },
    { label: "Barcode", sort: ["barcode"] },
    { label: "Last Updated", sort: ["updatedAt"] },
  ];

  if (!variables.hideInactive) {
    const extraCol: Column<keyof SupplyItemOrderBy> = { label: "Active", sort: ["active"] }
    cols.splice(1, 0, extraCol);
  }

  return <Box
    sx={{
      height: "100%",
      width: "100%",
      overflow: "hidden",
      mt: 4,
      mb: 4,
    }}
  >
    <Paper sx={{ width: '100%', p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">
          Supplies Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControlLabel
            control={<Switch
              checked={!variables.hideInactive}
              onChange={() => setVariables({ ...variables, hideInactive: variables.hideInactive ? null : true })}
            />}
            label="Show Inactive"
          />
          <PermissionOrHide permission={Permission.manageSupplies}>
            <Button variant="contained" size="medium" onClick={() => setAddSupplyItem(true)}>Add Supply Item</Button>
          </PermissionOrHide>
        </Box>
      </Box>
      <LazyLoadTable
        columns={cols}
        emptyLabel="No Supply Items to display"
        items={data?.supplyItem}
        loading={loading}
        queryVariables={variables}
        setQueryVariables={setVariables}
        renderRow={supplyItem => <SupplyItemRow
          key={supplyItem.id}
          supplyItem={supplyItem}
          hideInactive={!!variables.hideInactive}
        />}
      />
    </Paper>

    {addSupplyItem && <EditSupplyItem
      supplyItem={{ id: 0, label: "", barcode: "", currentStock: 0, active: true }}
      onClose={() => setAddSupplyItem(false)}
    />}
  </Box>
}

export default SupplyItemList;

const SupplyItemRow = ({ supplyItem, hideInactive }: { supplyItem: SupplyItemsPageQuery["supplyItem"][number], hideInactive: boolean }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(false);

  return <>
    <TableRow
      hover
      selected={!!selected}
      sx={{ cursor: "pointer" }}
      onClick={() => setSelected(true)}
    >
      <TableCell>
        {supplyItem.label}
        <PermissionOrHide permission={Permission.manageSupplies}>
          <IconButton
            size="small"
            onClick={e => { setEditOpen(true); e.stopPropagation() }}
            sx={{ minWidth: 'auto', padding: '4px', ml: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </PermissionOrHide>
      </TableCell>
      {!hideInactive && <TableCell>{supplyItem.active ? "Active" : "Inactive"}</TableCell>}
      <TableCell>
        {supplyItem.currentStock}
        {supplyItem.needsRecount && <Tooltip title="Please check actual stock level; there may be a miscount of remaining inventory">
          <WarningAmberRoundedIcon color="warning" sx={{ verticalAlign: "bottom", ml: 0.5 }} />
        </Tooltip>}
      </TableCell>
      <TableCell>{supplyItem.barcode}</TableCell>
      <LastUpdated supplyEvents={supplyItem.supplyEvents} />
    </TableRow>
    {editOpen && <EditSupplyItem supplyItem={supplyItem} onClose={() => setEditOpen(false)} />}
    {selected && <SupplyItemPopup supplyItem={supplyItem} onClose={() => setSelected(false)} />}
  </>
}

const LastUpdated = ({ supplyEvents }: { supplyEvents?: SupplyItemsPageQuery["supplyItem"][number]["supplyEvents"] }) => {
  const theme = useTheme();
  const { supplyChangeReasons } = useOrgConfig();

  if (!supplyEvents?.length) { return <TableCell>-</TableCell> }

  return <TableCell>
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center" }}>
      <Tooltip title={<div style={{ padding: "4px 8px 4px 4px" }}>
        Last few events:
        <ul style={{ margin: 0 }}>
          {supplyEvents.map((e, i) => {
            const reason = supplyChangeReasons[e.supplyChangeReasonId]
            return <li key={i}>
              {e.createdByUser?.firstName} {e.createdByUser?.lastName}{" "}
              {ACTION_LABEL[reason?.eventType ?? "recount"]}{" "}
              {e.amount}
            </li>
          })}
        </ul>
      </div>}>
        <span>{datef(new Date(supplyEvents[0].createdAt), 'MMM d, h:mm aa')}</span>
      </Tooltip>
        <AvatarGroup spacing={20}>
          {supplyEvents.map(e => {
            const user = e.createdByUser ?? { firstName: '', lastName: ''};
            return <Avatar key={e.id} sx={{ width: 24, height: 24, fontSize: "0.8rem", bgcolor: theme.palette.primary.main }}>
              {user.firstName[0]}{user.lastName[0]}
            </Avatar>
          })}
        </AvatarGroup>
    </Stack>
  </TableCell>
}

gql(`
  query SupplyItemsPage($offset: Int!, $orderBy: SupplyItemOrderBy!, $hideInactive: Boolean)
  @permission(requires: useSupplies)
  {
    supplyItem(
      offset: $offset
      limit: 50
      orderBy: $orderBy
      where: { active: { eq: $hideInactive } }
    ) {
      id
      label
      barcode
      currentStock
      needsRecount
      active
      updatedAt
      supplyEvents(limit: 3, orderBy: { id: { direction: desc, priority: 1 } }) {
        id
        supplyChangeReasonId
        amount
        createdAt
        createdByUser {
          firstName
          lastName
        }
      }
    }
  }
`);
